/* Speaker Wire Cable Picker — vanilla JS
 * GEARit gauge + conductor picker.
 * Gauge recommendation matrix matches spec exactly:
 *
 * Under 50W (low):
 *   5–25 ft  → 18 AWG CCA
 *   30–75 ft → 16 AWG CCA
 *   80–200ft → 14 AWG CCA
 *
 * 50–150W (medium):
 *   5–50 ft   → 14 AWG OFC
 *   55–95 ft  → 14 AWG OFC  (same AWG, kept explicit for rationale)
 *   100–200ft → 12 AWG OFC
 *
 * 150W+ (high):
 *   5–45 ft    → 14 AWG OFC
 *   50–115 ft  → 12 AWG OFC
 *   120–200 ft → 10 AWG OFC
 */
(function () {
  'use strict';

  /* ── Recommendation matrix ─────────────────────────────────────── */
  function recommendGauge(runFt, power) {
    if (power === 'low') {
      if (runFt <= 25) return {
        gauge: '18 AWG', conductor: 'CCA',
        rationale: 'Short, low-power run — 18 AWG CCA is plenty and saves money.',
        collectionKey: 'ccaUrl'
      };
      if (runFt <= 75) return {
        gauge: '16 AWG', conductor: 'CCA',
        rationale: 'Moderate run at low power — 16 AWG CCA keeps resistance in check.',
        collectionKey: 'ccaUrl'
      };
      return {
        gauge: '14 AWG', conductor: 'CCA',
        rationale: 'Long run even at low wattage — 14 AWG CCA prevents audible voltage drop.',
        collectionKey: 'ccaUrl'
      };
    }

    if (power === 'medium') {
      if (runFt <= 95) return {
        gauge: '14 AWG', conductor: 'OFC',
        rationale: runFt <= 50
          ? 'Short home-theater run — 14 AWG OFC is the sweet spot for 5.1 / 7.1.'
          : 'Mid-length home-theater run — 14 AWG OFC keeps voltage drop under 5%.',
        collectionKey: 'ofcUrl'
      };
      return {
        gauge: '12 AWG', conductor: 'OFC',
        rationale: 'Long run at medium power — step up to 12 AWG OFC to avoid voltage drop and preserve damping factor.',
        collectionKey: 'ofcUrl'
      };
    }

    /* high — 150W+ */
    if (runFt <= 45) return {
      gauge: '14 AWG', conductor: 'OFC',
      rationale: 'High-power amp, short run — 14 AWG OFC gives you full headroom.',
      collectionKey: 'ofcUrl'
    };
    if (runFt <= 115) return {
      gauge: '12 AWG', conductor: 'OFC',
      rationale: 'High-power amp over 50 ft — 12 AWG OFC keeps resistance low and dynamics clean.',
      collectionKey: 'ofcUrl'
    };
    return {
      gauge: '10 AWG', conductor: 'OFC',
      rationale: 'Long high-power run — 10 AWG OFC is the only way to maintain damping factor at this distance.',
      collectionKey: 'ofcUrl'
    };
  }

  /* ── Helpers ───────────────────────────────────────────────────── */
  function gaugeSlug(awg) {
    /* "14 AWG" → "14-awg-speaker-wire" */
    return awg.toLowerCase().replace(/\s+/g, '-') + '-speaker-wire';
  }

  function setSliderFill(input) {
    var min = parseFloat(input.min) || 5;
    var max = parseFloat(input.max) || 200;
    var val = parseFloat(input.value) || min;
    var pct = ((val - min) / (max - min) * 100).toFixed(1) + '%';
    input.style.setProperty('--fill', pct);
  }

  /* ── Conductor scenarios ───────────────────────────────────────── */
  var SCENARIOS = {
    'budget-short': {
      winner: 'cca',
      headline: 'CCA is the smart pick here.',
      reason: "Under 50 ft at low-to-medium power you won't measure a difference — and CCA saves 30–40%. Spend the budget on better speakers.",
      ctaUrlKey: 'ccaUrl',
      ctaLabel: 'Shop CCA speaker wire'
    },
    'home-theater': {
      winner: 'ofc',
      headline: 'OFC — the home-theater standard.',
      reason: '5.1 / 7.1 receivers and longer runs to surrounds reward pure copper. Lower resistance means cleaner dialogue, tighter bass, and full headroom on dynamic scenes.',
      ctaUrlKey: 'ofcUrl',
      ctaLabel: 'Shop OFC speaker wire'
    },
    'in-wall': {
      winner: 'ofc',
      headline: 'OFC with CL2 / CL3 jacket.',
      reason: 'In-wall installs need a code-rated jacket (CL2 indoor, CL3 outdoor/wet). Pair it with OFC so you never have to re-pull cable when you upgrade gear.',
      ctaUrlKey: 'inwallUrl',
      ctaLabel: 'Shop in-wall OFC'
    },
    'outdoor-pro': {
      winner: 'ofc',
      headline: 'OFC — no compromise.',
      reason: 'Direct-burial, long outdoor runs, and high-wattage pro amps all need pure copper. CCA corrodes faster outdoors and runs out of headroom over distance.',
      ctaUrlKey: 'cl3Url',
      ctaLabel: 'Shop CL3 outdoor OFC'
    }
  };

  /* ── Main init ─────────────────────────────────────────────────── */
  function initPicker(root) {
    var gaugeBase = root.getAttribute('data-gauge-base') || '/collections';
    var urls = {
      ccaUrl:    root.getAttribute('data-cca-url')    || '/collections/cca-copper-clad-aluminum-speaker-wire',
      ofcUrl:    root.getAttribute('data-ofc-url')    || '/collections/ofc-oxygen-free-copper-speaker-wire',
      inwallUrl: root.getAttribute('data-inwall-url') || '/collections/in-wall-speaker-wire-cl2',
      cl3Url:    root.getAttribute('data-cl3-url')    || '/collections/direct-burial-speaker-wire-cl3'
    };

    /* ── Gauge section ── */
    var runInput      = root.querySelector('[data-sw-run]');
    var runOut        = root.querySelector('[data-sw-run-out]');
    var gaugeOut      = root.querySelector('[data-sw-gauge-out]');
    var whyOut        = root.querySelector('[data-sw-why]');
    var gaugeCta      = root.querySelector('[data-sw-gauge-cta]');
    var gaugeCtaLabel = root.querySelector("[data-sw-gauge-cta-label]");
    var gaugeCtaConductor = root.querySelector("[data-sw-gauge-cta-conductor]");
    var powerBtns     = Array.prototype.slice.call(root.querySelectorAll('[data-sw-power]'));

    /* State — slider starts at 5 ft (leftmost), medium power */
    var state = { run: 5, power: 'medium' };

    /* Initialise slider value to 5 so it starts at the left */
    if (runInput) {
      runInput.value = '5';
      setSliderFill(runInput);
    }

    function syncGauge() {
      var rec = recommendGauge(state.run, state.power);

      /* 1. Update run length display */
      if (runOut) runOut.textContent = state.run;

      /* 2. Update slider fill track */
      if (runInput) setSliderFill(runInput);

      /* 3. Update recommendation outputs */
      if (gaugeOut)      gaugeOut.textContent = rec.gauge + ' ' + rec.conductor;
      if (gaugeCtaLabel) gaugeCtaLabel.textContent = rec.gauge;
      if (gaugeCtaConductor) gaugeCtaConductor.textContent = rec.conductor;
      if (whyOut)        whyOut.textContent = rec.rationale;

      /* 4. Update CTA href — go to specific gauge collection */
      if (gaugeCta) {
        var slug = gaugeSlug(rec.gauge);
        gaugeCta.setAttribute('href', gaugeBase.replace(/\/$/, '') + '/' + slug);
      }
    }

    /* Slider input event */
    if (runInput) {
      runInput.addEventListener('input', function () {
        state.run = parseInt(this.value, 10);
        syncGauge();
      });
    }

    /* Power button clicks */
    powerBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.power = btn.getAttribute('data-sw-power');
        powerBtns.forEach(function (b) {
          var isActive = b === btn;
          b.classList.toggle('is-active', isActive);
          b.setAttribute('aria-checked', isActive ? 'true' : 'false');
        });
        syncGauge();
      });
    });

    /* Set correct initial active button (medium) */
    powerBtns.forEach(function (b) {
      var isActive = b.getAttribute('data-sw-power') === state.power;
      b.classList.toggle('is-active', isActive);
      b.setAttribute('aria-checked', isActive ? 'true' : 'false');
    });

    /* Run initial sync */
    syncGauge();

    /* ── Conductor section ── */
    var condRoot      = root.querySelector('[data-sw-conductor]');
    if (!condRoot) return;

    var scenarioBtns  = Array.prototype.slice.call(condRoot.querySelectorAll('[data-sw-scenario]'));
    var winnerOut     = condRoot.querySelector('[data-sw-winner]');
    var headlineOut   = condRoot.querySelector('[data-sw-headline]');
    var reasonOut     = condRoot.querySelector('[data-sw-reason]');
    var condCta       = condRoot.querySelector('[data-sw-cond-cta]');
    var condCtaLabel  = condRoot.querySelector('[data-sw-cond-label]');
    var compare       = condRoot.querySelector('[data-sw-compare]');

    function applyScenario(id) {
      var s = SCENARIOS[id];
      if (!s) return;

      scenarioBtns.forEach(function (b) {
        var isActive = b.getAttribute('data-sw-scenario') === id;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });

      if (winnerOut)    winnerOut.textContent    = s.winner.toUpperCase();
      if (headlineOut)  headlineOut.textContent  = s.headline;
      if (reasonOut)    reasonOut.textContent    = s.reason;
      if (condCta)      condCta.setAttribute('href', urls[s.ctaUrlKey] || '#');
      if (condCtaLabel) condCtaLabel.textContent = s.ctaLabel;
      if (compare)      compare.setAttribute('data-winner', s.winner);
    }

    scenarioBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        applyScenario(btn.getAttribute('data-sw-scenario'));
      });
    });

    applyScenario('home-theater');
  }

  /* ── Bootstrap ─────────────────────────────────────────────────── */
  function init() {
    var pickers = document.querySelectorAll('[data-sw-picker]');
    Array.prototype.forEach.call(pickers, initPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();